import { ClusterVmhaStatus, VMHAStatusDetails } from 'api-client/model'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { AppSelector } from 'app/store'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import {
  blueprintsSelector,
  resmgrHostsSelector,
} from 'openstack/components/infrastructure/selectors'
import { getAggregatedUsage } from './helpers'
import { ClusterHostGroup, IClusterHostGroup, VMHADisplayStatus } from './model'

interface ClusterRoles {
  hypervisor: any[]
}

interface RolesByCluster {
  [Name: string]: ClusterRoles
}

export const clusterHostGroupsSelectorSimple: AppSelector<ClusterHostGroup[]> =
  createSharedSelector(
    getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
    (clusterHostGroups): ClusterHostGroup[] => {
      return clusterHostGroups.map((clusterHostGroup: ClusterHostGroup) => clusterHostGroup)
    },
  )

// PCD-11077: all-tenants counterpart of clusterHostGroupsSelectorSimple, for read-only screens
// (e.g. volume filter dropdowns) that only need bare cluster fields, not the full enrichment.
export const clusterHostGroupsAllTenantsSelectorSimple: AppSelector<ClusterHostGroup[]> =
  createSharedSelector(
    getDataSelector<DataKeys.ClusterHostGroupsAllTenants>(DataKeys.ClusterHostGroupsAllTenants),
    (clusterHostGroups): ClusterHostGroup[] => {
      return clusterHostGroups.map((clusterHostGroup: ClusterHostGroup) => clusterHostGroup)
    },
  )

const enrichClusterHostGroups = (
  clusterHostGroups: ClusterHostGroup[],
  clusterVMHAStatuses: VMHAStatusDetails[],
  resmgrHosts,
  blueprints,
): IClusterHostGroup[] => {
  const ephemeralStorageShared = !!blueprints?.[0]?.instanceSharedStorage
  return clusterHostGroups.map((clusterHostGroup: ClusterHostGroup) => {
    const hosts = resmgrHosts.filter((host) => clusterHostGroup?.hostlist?.includes(host.id))
    const physicalNetworkLabels = [
      ...new Set(hosts?.flatMap((host) => host?.physicalNetworkLabels || [])),
    ] as string[]

    const vmhaState =
      clusterVMHAStatuses?.find(
        (clusterVMHAStatus) => clusterVMHAStatus?.name === clusterHostGroup?.name,
      ) ?? ({} as VMHAStatusDetails)

    const vmhaStatus = (() => {
      if (!clusterHostGroup?.vmHighAvailability?.enabled) {
        return VMHADisplayStatus.DISABLED
      } else if (vmhaState?.vmha_status === ClusterVmhaStatus.Protected) {
        return VMHADisplayStatus.PROTECTED
      } else if (vmhaState?.vmha_status === ClusterVmhaStatus.Degraded) {
        return VMHADisplayStatus.DEGRADED
      } else if (vmhaState?.vmha_status === ClusterVmhaStatus.Unprotected) {
        return VMHADisplayStatus.NOT_PROTECTED
      } else {
        return VMHADisplayStatus.LOADING
      }
    })()

    return {
      ...clusterHostGroup,
      hosts,
      totalUsage: getAggregatedUsage(hosts, ephemeralStorageShared),
      physicalNetworkLabels,
      vmhaStatus,
      vmhaState,
    }
  })
}

export const clusterHostGroupsSelector: AppSelector<IClusterHostGroup[]> = createSharedSelector(
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  getDataSelector<DataKeys.ClusterVMHAStatus>(DataKeys.ClusterVMHAStatus),
  resmgrHostsSelector,
  blueprintsSelector,
  enrichClusterHostGroups,
)

// PCD-11077: backs read-only admin screens that need every cluster regardless of the current
// user's tenant access (the server-side `/clusters` filter is bypassed via `all_tenants=true`,
// see actions.ts#listClusterHostGroupsAllTenants). Action flows and pickers should keep using
// `clusterHostGroupsSelector` above, which reflects the backend's tenant-filtered list.
export const clusterHostGroupsAllTenantsSelector: AppSelector<IClusterHostGroup[]> =
  createSharedSelector(
    getDataSelector<DataKeys.ClusterHostGroupsAllTenants>(DataKeys.ClusterHostGroupsAllTenants),
    getDataSelector<DataKeys.ClusterVMHAStatus>(DataKeys.ClusterVMHAStatus),
    resmgrHostsSelector,
    blueprintsSelector,
    enrichClusterHostGroups,
  )

export const activeRoleHostsByCluster: AppSelector<RolesByCluster> = createSharedSelector(
  resmgrHostsSelector,
  clusterHostGroupsAllTenantsSelector,
  (hosts, clusters) => {
    // In the future, will need for storage hosts too
    const activeHypervisors = hosts.filter(
      (host) => host?.schedulingStatus === CLUSTER_HOST_SCHEDULING.enabled,
    )
    const activeHypervisorIds = activeHypervisors.map((host) => host.id)
    return clusters.reduce((accum, cluster) => {
      return {
        ...accum,
        [cluster.name]: {
          hypervisor: (cluster?.hosts || [])?.filter((host) =>
            activeHypervisorIds.includes(host?.id),
          ),
        },
      }
    }, {})
  },
)

export const clusterVMHAStatusSelector: AppSelector<VMHAStatusDetails[]> = createSharedSelector(
  getDataSelector<DataKeys.ClusterVMHAStatus>(DataKeys.ClusterVMHAStatus),
  (clusterVMHAStatuses): VMHAStatusDetails[] => {
    return clusterVMHAStatuses.map((clusterVMHAStatus: VMHAStatusDetails) => {
      return {
        ...clusterVMHAStatus,
      }
    })
  },
)
